import { HYBRID_ADDON_BUNDLE_SKUS, HYBRID_BUNDLE_SKUS, HYBRID_RACK_BUNDLE_SKUS, HYBRID_RIG_SKUS } from '@/js/pages/product/rackbuilder/products/rig/hybrid/hybridConstants'
import { getHybridUnitLabel, isHybridRackLevelOption } from '@/js/pages/product/rackbuilder/products/rig/hybrid/hybridUtils'
import { EMAIL_RACK_KEY, EMAIL_SECTION_KEY, EMAIL_UNIT_KEY } from '../emailKeys'
import type { BuyRequest, IRogue } from '@/js/services/RogueTypes'
import type { CustomProductVariant } from '../types'

// The PDP identifies the product by its group sku; the cart only ever sees the two bundles.
const HYBRID_ALL_SKUS: readonly string[] = [...HYBRID_RIG_SKUS, ...HYBRID_BUNDLE_SKUS]

// Compared by value: lib-payments hands the hook the same object it iterates today, but a clone upstream would otherwise silently drop every header with no error.
function isSameLine(lineA: BuyRequest | undefined, lineB: BuyRequest | undefined): boolean {
  return !!lineA && !!lineB && lineA.productId === lineB.productId && lineA.metadata?.bundleOptionId === lineB.metadata?.bundleOptionId
}

// Rack-level rows other than the Rig itself (today: Plate Storage), in bundle-option order - the order m2-checkout re-sorts a bundle's children into.
function getHybridRackItems(rogue: IRogue, rack: BuyRequest): BuyRequest[] {
  const bundleOptions = rogue.getProductById(rack.productId)?.bundleOptions ?? []
  const optionIndex = (child: BuyRequest) => bundleOptions.findIndex(opt => opt.id === child.metadata?.bundleOptionId)

  return (rack.children ?? [])
    .filter(child => !isSameLine(child, rack.children?.[0]) && child.qty > 0 && isHybridRackLevelOption(bundleOptions[optionIndex(child)]))
    .sort((childA, childB) => optionIndex(childA) - optionIndex(childB))
}

export const hybridRigVariant: CustomProductVariant = {
  id: 'hybrid-rig',

  matches: (product, groupProduct) => {
    const sku = product?.sku
    const groupSku = groupProduct?.sku

    return (!!sku && HYBRID_ALL_SKUS.includes(sku)) || (!!groupSku && HYBRID_ALL_SKUS.includes(groupSku))
  },

  skuSets: {
    allSkus              : HYBRID_ALL_SKUS,
    itemSkus             : HYBRID_BUNDLE_SKUS,
    mainSkus             : HYBRID_RACK_BUNDLE_SKUS,
    // Only the units are excluded, so the rack bundle still moves the cart badge — same as Cave and Slinger X.
    qtyExcludedSkus      : HYBRID_ADDON_BUNDLE_SKUS,
    addCountExcludedSkus : HYBRID_ADDON_BUNDLE_SKUS,
  },

  // Hybrid joins its unit siblings via metadata.recipe, same as Cave and MS/MMS.
  purchaseKey: (item) => item.metadata?.recipe,

  // The rack is Unit 1 and the added units (the children carrying a side) follow from 2, in the order they were chained on; m2-checkout puts unnumbered lines last, so an unnumbered rack would trail its own units in the order and the email.
  prepareForCart: (buyRequest) => {
    const [rack, ...rest] = buyRequest.children ?? []

    if (!rack) return

    rack.metadata ??= {}
    rack.metadata.unitNumber = 1

    rest.filter(child => child.metadata?.side).forEach((unit, index) => {
      unit.metadata!.unitNumber = index + 2
    })
  },

  // prepareForCart numbers the rack line 1 and units from 2, so unitNumber - 1 is the label index everywhere; a rack line from before that numbering still reads as Unit 1.
  emailLayout: (rogue, unit, child) => {
    const unitSku = rogue.getProductById(unit.productId)?.sku || unit.sku
    const isRack = !!unitSku && HYBRID_RACK_BUNDLE_SKUS.includes(unitSku)
    const unitNumber = unit.metadata?.unitNumber

    if (isSameLine(child, unit.children?.[0])) {
      const index = typeof unitNumber === 'number' ? unitNumber - 1 : (isRack ? 0 : undefined)

      return index === undefined ? undefined : { [EMAIL_UNIT_KEY]: getHybridUnitLabel(rogue, unit, index) }
    }

    // Rack-level items lead the email under their own Rack header, as Cave's do, instead of reading as Unit 1 selections; the header rides on the first of them.
    if (!isRack) return undefined

    const rackItems = getHybridRackItems(rogue, unit)

    if (!rackItems.some(item => isSameLine(item, child))) return undefined

    const configuration: Record<string, string> = { [EMAIL_RACK_KEY]: '1' }

    if (isSameLine(child, rackItems[0])) configuration[EMAIL_SECTION_KEY] = rogue.__('Rack')

    return configuration
  },
}
